import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format, addDays, isBefore, isValid } from 'date-fns';
import { IoIosArrowBack, IoMdInformationCircle } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Button, Input, InputDate, Layout, Select } from '~/components';
import { useAppBillingData } from '~/hooks/billingData';
import { useScreen } from '~/hooks/screen';
import { useStudent } from '~/hooks/student';
import api from '~/services/api';
import { cpfFormatter } from '~/utils/formatter';
import getValidationErrors from '~/utils/getValidationErrors';

import Modal from './Modal';

import {
  ModalContainer,
  ContentContainer,
  RowContent,
  SecondPageHeader,
  SecondaryHeaderLine,
  SecondaryHeaderBackButton,
  SecondaryHeaderContinueButton,
  SecondaryHeaderInfo,
  InfoButton,
  Container,
  RowContainer,
  InputSet,
  InputLabel,
  FormWarning,
} from './styles';

type Training = {
  delivery_date: string;
  delivery_date_2?: string;
  emition_date: string;
  cycle: number;
  next_training_date: string;
  deadline: number;
  status: string;
};

type SelectOption = {
  value: string;
  label: string;
};

type RouteParams = {
  userId: string;
  billingId: string;
  programId: string;
};

const dateParser = {
  br: (date: Date) => format(date, 'dd/MM/yyyy'),
  en: (date: string) => {
    const initialDate = date.split('/');

    return new Date(`${initialDate[1]}/${initialDate[0]}/${initialDate[2]}`);
  },
  ymd: (date: Date) => format(date, 'yyyy/MM/dd'),
};

function TrainingManagement() {
  const { goBack } = useHistory();
  const { userId: studentId, billingId, programId } = useParams<RouteParams>();
  const { updateScreen } = useScreen();
  const { studentData, updateStudent } = useStudent();
  const { currentProgram, billingData } = useAppBillingData();

  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);
  const [defaultStatus, setDefaultStatus] = useState<SelectOption>();
  const [scheduledDeliver, SetscheduledDeliver] = useState<string>();

  const trainingStatusVariations = useMemo(
    () => [
      {
        value: 'pending',
        label: 'Pendente',
      },
      {
        value: 'emited',
        label: 'Emitido',
      },
      {
        value: 'canceled',
        label: 'Cancelado',
      },
      {
        value: 'expired',
        label: 'Expirado',
      },
    ],
    [],
  );
  const numberOfPrograms = useMemo(
    () => billingData.package?.number_of_training_programs,
    [billingData],
  );
  const numberOfThisProgram = useMemo(
    () => currentProgram.number_of_program,
    [currentProgram],
  );
  const lastProgram = useMemo(
    () => numberOfPrograms === numberOfThisProgram,
    [numberOfPrograms, numberOfThisProgram],
  );

  useEffect(() => {
    updateScreen(`trainingPrograms`);
    updateStudent(studentId);
  }, [updateScreen, updateStudent, studentId]);

  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get<Training>(
        `programs/${programId}/manangers/any_id`,
      );

      if (data.delivery_date !== null) {
        SetscheduledDeliver(
          dateParser.br(addDays(new Date(data?.delivery_date), 1)),
        );
      }

      setDefaultStatus(
        trainingStatusVariations.filter(
          (status) => status.value === data.status,
        )[0],
      );

      const formInitialData = {
        ...data,
        emition_date: format(
          addDays(new Date(data.emition_date), 1),
          'yyyy-MM-dd',
        ),
      };

      if (data.next_training_date) {
        formInitialData.next_training_date = lastProgram
          ? ''
          : format(addDays(new Date(data.next_training_date), 1), 'yyyy-MM-dd');
      }

      if (data.delivery_date) {
        formInitialData.delivery_date_2 = format(
          addDays(new Date(data.delivery_date), 1),
          'yyyy-MM-dd',
        );
      }

      formRef.current?.setData(formInitialData);
    }

    fetchData();
  }, [lastProgram, programId, trainingStatusVariations]);

  const updateNextTrainingDate = useCallback(() => {
    const cycle = formRef.current?.getFieldValue('cycle');

    if (cycle && !Number.isNaN(Number(cycle))) {
      const createdAt = formRef.current?.getFieldValue('emition_date');

      const valueToVerify = new Date(createdAt);

      const isValidDate = isValid(valueToVerify);

      if (isValidDate) {
        const newValue = lastProgram
          ? ''
          : format(addDays(new Date(createdAt), +cycle + 1), 'yyyy-MM-dd');

        formRef.current?.setFieldValue('next_training_date', newValue);
      }
    }
  }, [lastProgram]);

  const [showInfoModal, setShowInfoModal] = useState(false);

  function InfoModal() {
    return (
      <Modal
        isOpen={showInfoModal}
        setOpen={setShowInfoModal}
        maxWidthStyled={600}
        maxHeightStyled={600}
      >
        <ModalContainer>
          <h1>Informações</h1>

          <ContentContainer>
            <RowContent>
              <strong>Fatura: </strong>
              <p>{billingId}</p>
            </RowContent>

            <RowContent>
              <strong>Programa: </strong>
              <p>{programId}</p>
            </RowContent>

            <RowContent>
              <strong>Nome do Aluno: </strong>
              <p>{studentData.name}</p>
            </RowContent>

            <RowContent>
              <strong>CPF: </strong>
              <p>{cpfFormatter(studentData.student.cpf)}</p>
            </RowContent>
          </ContentContainer>
        </ModalContainer>
      </Modal>
    );
  }

  const [maxLengthWarnModal, setMaxLengthWarnModal] = useState(false);
  function MaxLengthWarnModal() {
    const handleCloseModal = () => setMaxLengthWarnModal(false);

    return (
      <Modal
        isOpen={maxLengthWarnModal}
        setOpen={setMaxLengthWarnModal}
        maxWidthStyled={700}
        maxHeightStyled={300}
      >
        <ModalContainer>
          <h1>Permine no mínimo 1 dia e máximo 10 dias</h1>

          <RowContent>
            <Button onClick={handleCloseModal} style={{ padding: '0 2rem' }}>
              Confirma
            </Button>
          </RowContent>
        </ModalContainer>
      </Modal>
    );
  }

  const verifyDeadlineNumberRange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;

    if (Number(value) > 10) {
      setMaxLengthWarnModal(true);
      formRef.current?.setFieldValue('deadline', 10);
    }
    if (Number(value) < 0) {
      setMaxLengthWarnModal(true);
      formRef.current?.setFieldValue('deadline', 1);
    }
  };

  const handleSubmit = useCallback(
    async (data: Training) => {
      const formData = {
        ...data,
        next_training_date: !lastProgram
          ? format(
              addDays(new Date(data.next_training_date), 1),
              'yyyy-MM-dd hh:mm:ss',
            )
          : '',
        delivery_date: data.delivery_date_2,
      };

      console.log('formData:', formData);

      delete formData.delivery_date_2;

      const schema = Yup.object().shape({
        cycle: Yup.number()
          .required('O campo é obrigatório!')
          .min(1, 'O valor precisa ser entre 1 e 100')
          .max(100, 'O valor precisa ser entre 1 e 100'),
        next_training_date: Yup.string().required('O campo é obrigatório!'),
        deadline: Yup.number()
          .required('O campo é obrigatório!')
          .min(1, 'O valor precisa ser entre 1 e 10')
          .max(10, 'O valor precisa ser entre 1 e 10'),
        status: Yup.string().required('O campo é obrigatório!'),
      });

      const lastProgramSchema = Yup.object().shape({
        cycle: Yup.string(),
        next_training_date: Yup.string(),
        deadline: Yup.number()
          .required('O campo é obrigatório!')
          .min(1, 'O valor precisa ser entre 1 e 10')
          .max(10, 'O valor precisa ser entre 1 e 10'),
        status: Yup.string().required('O campo é obrigatório!'),
      });

      try {
        if (lastProgram) {
          await lastProgramSchema.validate(formData, { abortEarly: false });
        } else {
          await schema.validate(formData, { abortEarly: false });
        }

        if (
          isBefore(
            new Date(formData.next_training_date),
            new Date(data.delivery_date),
          )
        ) {
          toast.error(
            `A data do próximo treino não pode ser anterior a data de emissão!`,
          );

          return;
        }

        if (!lastProgram && +data.deadline >= +data.cycle) {
          toast.error(`O deadline não pode ser maior ou igual ao ciclo!`);

          return;
        }

        await api.put(`programs/${programId}/manangers/any_id`, formData);

        toast.success('Gestão de treino cadastrado com sucesso!');

        goBack();
      } catch (err) {
        console.log(err);
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        toast.error('Erro ao cadastrar gestão de treino!');
      }
    },
    [programId, goBack, lastProgram],
  );

  const inputStyle = {
    input: {
      height: '3.5rem',
      padding: '0 1.7rem',
    },
    textArea: {
      height: '15rem',
      padding: '2rem',
      borderRadius: '1rem',
    },
    select: {
      placeholder: () => ({
        padding: '0 1rem',
        color: '#00000080',
      }),
      singleValue: () => ({
        padding: '0 1rem',
      }),
    },
  };

  useEffect(() => {
    try {
      if (billingData.created_at) {
        const date = addDays(
          dateParser.en(dateParser.br(new Date(billingData.created_at))),
          Number(4),
        );

        SetscheduledDeliver(format(date, 'yyyy-MM-dd'));
        return;
      }
    } catch (e) {
      SetscheduledDeliver(undefined);
      if (loading) {
        setLoading(false);

        toast.error('Preencha todos os dados corretamente!');
      }
    }
  }, [loading]);

  return (
    <Layout title="Aluno">
      <MaxLengthWarnModal />

      <SecondPageHeader>
        <SecondaryHeaderLine>
          <SecondaryHeaderBackButton onClick={() => goBack()}>
            <IoIosArrowBack />

            <p>Gestão do treino</p>
          </SecondaryHeaderBackButton>

          <SecondaryHeaderContinueButton
            disabled={loading}
            onClick={() => formRef.current?.submitForm()}
          >
            Salvar
          </SecondaryHeaderContinueButton>
        </SecondaryHeaderLine>

        <SecondaryHeaderLine>
          <InfoModal />

          <SecondaryHeaderInfo>
            <div>
              <strong>Nome: </strong>

              <p>{studentData.name}</p>
            </div>

            <InfoButton type="button" onClick={() => setShowInfoModal(true)}>
              <IoMdInformationCircle color="#ffdf22" size={24} />
            </InfoButton>
          </SecondaryHeaderInfo>
        </SecondaryHeaderLine>
      </SecondPageHeader>

      <Container>
        <Form ref={formRef} onSubmit={handleSubmit}>
          {lastProgram && (
            <RowContainer>
              <FormWarning>*Aviso: Este é o último programa</FormWarning>
            </RowContainer>
          )}

          <RowContainer>
            <InputSet>
              <InputLabel>Entrega programada:</InputLabel>;
              <InputDate
                name="delivery_date_2"
                value={scheduledDeliver}
                placeholder="dd/mm/aaaa"
                containerStyle={inputStyle.input}
                style={{
                  color: `${lastProgram ? 'red' : '#000'}`,
                  fontSize: '1.5rem',
                }}
                disabled
              />
            </InputSet>

            <InputSet>
              <InputLabel>Data de Emissão:*</InputLabel>
              <InputDate
                name="emition_date"
                onChange={updateNextTrainingDate}
                placeholder="dd/mm/aaaa"
                containerStyle={inputStyle.input}
                style={{
                  color: `${lastProgram ? 'red' : '#000'}`,
                  fontSize: '1.5rem',
                }}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>Ciclo (em dias):*</InputLabel>
              <Input
                name="cycle"
                type="number"
                placeholder="ex. 2 dias"
                min={0}
                max={100}
                disabled={lastProgram}
                onChange={updateNextTrainingDate}
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </InputSet>

            <InputSet>
              <InputLabel>Data do próximo treino:</InputLabel>
              <InputDate
                name="next_training_date"
                placeholder="dd/mm/aaaa"
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
                disabled={lastProgram}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>(Gestão do prazo de entrega) Deadline:*</InputLabel>
              <Input
                type="number"
                min={1}
                max={10}
                name="deadline"
                placeholder="Max. 10 dias"
                onChange={verifyDeadlineNumberRange}
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </InputSet>

            <InputSet>
              <InputLabel>Status do treino:</InputLabel>
              <Select
                key={`force_reenter_key_${defaultStatus?.label}`}
                name="status"
                defaultValue={defaultStatus || trainingStatusVariations[0]}
                options={trainingStatusVariations}
                isSearchable={false}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>
          </RowContainer>
        </Form>
      </Container>
    </Layout>
  );
}

export default TrainingManagement;
